import { useResellers } from '@/hooks/useWow';
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map,
  Pin,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { useState } from 'react';

export type Reseller = {
  id: number;
  name: string;
  lat: number;
  lon: number;
};

const API_KEY = process.env.GOOGLE_MAPS_API_KEY;

const SWEDEN_CENTER = { lat: 62.5, lng: 16.5 };

function ResellerMarker({
  reseller,
  open,
  onOpen,
  onConfirm,
}: {
  reseller: Reseller;
  open: boolean;
  onOpen: (id: number | null) => void;
  onConfirm: (reseller: Reseller) => void;
}) {
  const [markerRef, marker] = useAdvancedMarkerRef();

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{ lat: reseller.lat, lng: reseller.lon }}
        title={reseller.name}
        onClick={() => onOpen(open ? null : reseller.id)}
      >
        <Pin
          background={open ? '#05141f' : 'black'}
          borderColor="#05141f"
          glyphColor="#ffffff"
        />
      </AdvancedMarker>
      {open && marker && (
        <InfoWindow anchor={marker} onClose={() => onOpen(null)}>
          <div className="flex flex-col gap-2 min-w-[160px] overflow-hidden">
            <p className="font-medium text-sm text-[#05141f]">
              {reseller.name}
            </p>
            <button
              type="button"
              onClick={() => onConfirm(reseller)}
              className="w-full px-3 py-1.5 bg-[#05141f] text-white text-xs font-medium hover:bg-black transition-colors"
            >
              Välj återförsäljare
            </button>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

function GoogleMap({
  onSelectReseller,
}: {
  onSelectReseller?: (reseller: Reseller | null) => void;
}) {
  const { data, isLoading } = useResellers();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  if (isLoading) return null;

  const resellers: Reseller[] = (data ?? []).filter(
    (d: Reseller) => d.lat != null && d.lon != null,
  );

  const handleConfirm = (reseller: Reseller) => {
    setSelectedId(reseller.id);
    onSelectReseller?.(reseller);
  };

  return (
    <APIProvider apiKey={API_KEY}>
      <Map
        className="w-full h-full"
        defaultCenter={SWEDEN_CENTER}
        defaultZoom={5}
        gestureHandling="greedy"
        disableDefaultUI
        mapId="kia-resellers"
      >
        {resellers.map((reseller) => (
          <ResellerMarker
            key={reseller.id}
            reseller={reseller}
            open={reseller.id === selectedId}
            onOpen={setSelectedId}
            onConfirm={handleConfirm}
          />
        ))}
      </Map>
    </APIProvider>
  );
}

export default GoogleMap;
